/**
 * Trovva — AI Follow-Up Modal
 *
 * Generate and send AI-powered follow-up messages (email or SMS).
 * Multi-step: configure → review/edit → sending → sent confirmation.
 *
 * Uses inline styles (no Tailwind) to match the rest of the Trovva codebase.
 * Renders its own fixed overlay so it works correctly whether opened from
 * LeadDetailDrawer, ContactDetailView, or anywhere else.
 */
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';

const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'urgent', label: 'Urgent' },
  { id: 'casual', label: 'Casual' },
];

const CHANNELS = [
  { id: 'email', label: 'Email', icon: '\u2709\uFE0F' },
  { id: 'sms', label: 'SMS', icon: '\uD83D\uDCAC' },
];

const LENGTHS = [
  { id: 'short', label: 'Short' },
  { id: 'medium', label: 'Medium' },
  { id: 'long', label: 'Detailed' },
];

export default function AIFollowUpModal({ contact, onClose, onSent }) {
  const [step, setStep] = useState('configure'); // configure | review | sending | sent
  const [channel, setChannel] = useState('email');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [context, setContext] = useState('');
  const [generated, setGenerated] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not signed in');
    return {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    };
  };

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch('/api/ai-followup', {
        method: 'POST', headers,
        body: JSON.stringify({
          action: 'generate',
          contact_id: contact.id,
          channel, tone, length,
          additional_context: context,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setGenerated(data);
        setSubject(data.subject || '');
        setBody(data.body || data.message || '');
        setStep('review');
      } else {
        const err = await resp.json();
        setError(err.error || 'Failed to generate');
      }
    } catch (e) {
      setError(e.message || 'Network error');
    }
    setGenerating(false);
  };

  const send = async () => {
    setStep('sending');
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch('/api/ai-followup', {
        method: 'POST', headers,
        body: JSON.stringify({
          action: 'send',
          contact_id: contact.id,
          channel, subject, body,
        }),
      });
      if (resp.ok) {
        setStep('sent');
        setTimeout(() => { onSent?.(); onClose(); }, 1500);
      } else {
        setError('Failed to send');
        setStep('review');
      }
    } catch (e) {
      setError('Network error');
      setStep('review');
    }
  };

  const contactName = contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '—';
  const contactInitial = (contact.first_name || contact.full_name || '?')[0].toUpperCase();

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.colors.card,
          borderRadius: 16,
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>{'\u2728'}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>AI Follow-Up</span>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 20, color: theme.colors.textLight, lineHeight: 1,
            padding: '4px 6px', borderRadius: 6,
          }}
            onMouseEnter={e => e.currentTarget.style.color = theme.colors.text}
            onMouseLeave={e => e.currentTarget.style.color = theme.colors.textLight}
          >&times;</button>
        </div>

        {/* ── Contact info strip ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 20px', background: '#F8FAFC',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 16,
            background: `${theme.colors.primary}20`, color: theme.colors.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}>{contactInitial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{contactName}</div>
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>{contact.email || contact.phone || ''}</div>
          </div>
          {contact.lead_score != null && (
            <span style={{
              padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              background: `${theme.colors.primary}15`, color: theme.colors.primary,
            }}>Score: {contact.lead_score}</span>
          )}
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '20px 20px 24px' }}>

          {/* Step: Configure */}
          {step === 'configure' && (
            <div>
              {/* Channel */}
              <OptionGroup label="Channel">
                <div style={{ display: 'flex', gap: 8 }}>
                  {CHANNELS.map(c => (
                    <PillButton key={c.id} active={channel === c.id} onClick={() => setChannel(c.id)} style={{ flex: 1 }}>
                      {c.icon} {c.label}
                    </PillButton>
                  ))}
                </div>
              </OptionGroup>

              {/* Tone */}
              <OptionGroup label="Tone">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {TONES.map(t => (
                    <PillButton key={t.id} active={tone === t.id} onClick={() => setTone(t.id)}>
                      {t.label}
                    </PillButton>
                  ))}
                </div>
              </OptionGroup>

              {/* Length */}
              <OptionGroup label="Length">
                <div style={{ display: 'flex', gap: 6 }}>
                  {LENGTHS.map(l => (
                    <PillButton key={l.id} active={length === l.id} onClick={() => setLength(l.id)}>
                      {l.label}
                    </PillButton>
                  ))}
                </div>
              </OptionGroup>

              {/* Context */}
              <OptionGroup label="Additional Context (optional)">
                <textarea
                  value={context}
                  onChange={e => setContext(e.target.value)}
                  placeholder="e.g., They just visited an open house at 45 Oak Lane..."
                  rows={3}
                  style={inputStyle()}
                />
              </OptionGroup>

              {error && <div style={{ fontSize: 12, color: theme.colors.danger, marginBottom: 12 }}>{error}</div>}

              <button onClick={generate} disabled={generating} style={{
                width: '100%', padding: '10px 16px', borderRadius: 10, border: 'none',
                background: generating ? theme.colors.textLight : `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.primary})`,
                color: 'white', fontSize: 13, fontWeight: 700, cursor: generating ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: generating ? 0.7 : 1,
              }}>
                {generating ? (
                  <><Spinner /> Generating...</>
                ) : (
                  <>{'\u2728'} Generate Follow-Up</>
                )}
              </button>
            </div>
          )}

          {/* Step: Review */}
          {step === 'review' && (
            <div>
              {channel === 'email' && (
                <OptionGroup label="Subject">
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    style={inputStyle()}
                  />
                </OptionGroup>
              )}

              <OptionGroup label="Message">
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={8}
                  style={inputStyle()}
                />
              </OptionGroup>

              {error && <div style={{ fontSize: 12, color: theme.colors.danger, marginBottom: 12 }}>{error}</div>}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep('configure')} style={{
                  flex: 1, padding: '10px 16px', borderRadius: 10,
                  border: `1px solid ${theme.colors.border}`, background: theme.colors.card,
                  color: theme.colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F5F7FA'}
                  onMouseLeave={e => e.currentTarget.style.background = theme.colors.card}
                >
                  Regenerate
                </button>
                <button onClick={send} style={{
                  flex: 1, padding: '10px 16px', borderRadius: 10, border: 'none',
                  background: `linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.primary})`,
                  color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                  {channel === 'email' ? '\u2709\uFE0F' : '\uD83D\uDCAC'} Send {channel === 'email' ? 'Email' : 'SMS'}
                </button>
              </div>
            </div>
          )}

          {/* Step: Sending */}
          {step === 'sending' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Spinner size={28} />
              <div style={{ marginTop: 12, fontSize: 13, color: theme.colors.textLight }}>Sending...</div>
            </div>
          )}

          {/* Step: Sent */}
          {step === 'sent' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>{'\u2705'}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>Message sent!</div>
              <div style={{ fontSize: 12, color: theme.colors.textLight, marginTop: 4 }}>
                Follow-up delivered via {channel}
              </div>
            </div>
          )}
        </div>

        <style>{`@keyframes aifu-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
      </div>
    </div>
  );
}

/* ── Shared sub-components ── */

function OptionGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: theme.colors.textLight,
        textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
      }}>{label}</div>
      {children}
    </div>
  );
}

function PillButton({ active, onClick, children, style = {} }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
      cursor: 'pointer', transition: 'all 0.15s',
      border: active ? `1.5px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
      background: active ? `${theme.colors.primary}10` : theme.colors.card,
      color: active ? theme.colors.primary : theme.colors.text,
      ...style,
    }}>{children}</button>
  );
}

function Spinner({ size = 16 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid ${theme.colors.border}`,
      borderTopColor: theme.colors.primary,
      animation: 'aifu-spin 0.8s linear infinite',
      display: 'inline-block',
    }} />
  );
}

function inputStyle() {
  return {
    width: '100%', padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${theme.colors.border}`,
    fontSize: 13, color: theme.colors.text, background: theme.colors.card,
    fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
  };
}
